import React, { useState, useEffect } from 'react';
import { BarChart3, Calendar, TrendingUp, Heart, Eye } from 'lucide-react';
import { Button, Card, Spinner, Alert } from '../design-system';
import moodTrackerService from '../../services/moodTrackerService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * Mood Analytics Summary Component
 * Shows basic mood statistics on the dashboard
 */
const MoodAnalyticsSummary = ({ onViewDetails }) => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadMoodAnalytics();
  }, []);

  const loadMoodAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get mood history for the last 30 days
      const moodHistory = await moodTrackerService.getMoodHistory('month', user);
      
      const analytics = calculateMoodAnalytics(moodHistory);
      setAnalytics(analytics);
    } catch (error) {
      console.error('Error loading mood analytics:', error);
      setError('Failed to load mood analytics');
      // Don't show error toast for analytics, it's secondary
    } finally {
      setLoading(false);
    }
  };

  const calculateMoodAnalytics = (moodHistory) => {
    if (!moodHistory || moodHistory.length === 0) {
      return {
        totalEntries: 0,
        avgMood: 0,
        streak: 0,
        recentTrend: 'stable'
      };
    }

    // Calculate total entries
    const totalEntries = moodHistory.length;

    // Calculate average mood (assuming mood values are 1-5)
    const moodValues = moodHistory.map(entry => {
      const moodMap = { 'very-sad': 1, 'sad': 2, 'neutral': 3, 'happy': 4, 'very-happy': 5 };
      return moodMap[entry.mood] || 3;
    });
    const avgMood = moodValues.reduce((sum, val) => sum + val, 0) / moodValues.length;

    // Calculate current streak (consecutive days with mood entries)
    const sortedByDate = moodHistory
      .map(entry => ({
        ...entry,
        localDate: moodTrackerService.getLocalDateString(entry.createdAt || entry.timestamp || entry.date)
      }))
      .sort((a, b) => b.localDate.localeCompare(a.localDate));

    let streak = 0;
    const today = new Date().toISOString().split('T')[0];
    let currentDate = today;

    for (const entry of sortedByDate) {
      if (entry.localDate === currentDate) {
        streak++;
        // Move to previous day
        const prevDate = new Date(currentDate);
        prevDate.setDate(prevDate.getDate() - 1);
        currentDate = prevDate.toISOString().split('T')[0];
      } else {
        break;
      }
    }

    // Calculate recent trend (last 7 days vs previous 7 days)
    const last7Days = moodHistory
      .filter(entry => {
        const entryDate = new Date(entry.createdAt || entry.timestamp || entry.date);
        const daysDiff = (new Date() - entryDate) / (1000 * 60 * 60 * 24);
        return daysDiff <= 7;
      })
      .map(entry => {
        const moodMap = { 'very-sad': 1, 'sad': 2, 'neutral': 3, 'happy': 4, 'very-happy': 5 };
        return moodMap[entry.mood] || 3;
      });

    const previous7Days = moodHistory
      .filter(entry => {
        const entryDate = new Date(entry.createdAt || entry.timestamp || entry.date);
        const daysDiff = (new Date() - entryDate) / (1000 * 60 * 60 * 24);
        return daysDiff > 7 && daysDiff <= 14;
      })
      .map(entry => {
        const moodMap = { 'very-sad': 1, 'sad': 2, 'neutral': 3, 'happy': 4, 'very-happy': 5 };
        return moodMap[entry.mood] || 3;
      });

    let recentTrend = 'stable';
    if (last7Days.length > 0 && previous7Days.length > 0) {
      const last7Avg = last7Days.reduce((sum, val) => sum + val, 0) / last7Days.length;
      const prev7Avg = previous7Days.reduce((sum, val) => sum + val, 0) / previous7Days.length;
      
      if (last7Avg > prev7Avg + 0.2) {
        recentTrend = 'improving';
      } else if (last7Avg < prev7Avg - 0.2) {
        recentTrend = 'declining';
      }
    }

    return {
      totalEntries,
      avgMood: Math.round(avgMood * 10) / 10, // Round to 1 decimal
      streak,
      recentTrend
    };
  };

  const getMoodEmoji = (avgMood) => {
    if (avgMood >= 4.5) return '😊';
    if (avgMood >= 3.5) return '🙂';
    if (avgMood >= 2.5) return '😐';
    if (avgMood >= 1.5) return '😔';
    return '😢';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return '📈';
      case 'declining': return '📉';
      default: return '➡️';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'improving': return 'text-green-600';
      case 'declining': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <Card padding="large">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3">
            <Spinner size="medium" />
            <span className="text-gray-600">Loading mood analytics...</span>
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card padding="large">
        <Alert variant="error">
          {error}
        </Alert>
      </Card>
    );
  }

  if (!analytics || analytics.totalEntries === 0) {
    return (
      <Card padding="large">
        <div className="text-center py-8">
          <div className="text-4xl mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Your Mood Journey
          </h3>
          <p className="text-gray-600 mb-4">
            Track your daily mood patterns and see how your emotional well-being changes over time.
          </p>
          <Button
            onClick={onViewDetails}
            size="small"
            className="mx-auto"
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="large">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 rounded-full flex items-center justify-center bg-blue-100">
            <BarChart3 className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Mood Analytics</h2>
            <p className="text-sm text-gray-600">Track your emotional journey</p>
          </div>
        </div>
        <Button
          onClick={onViewDetails}
          variant="outline"
          size="small"
        >
          <Eye className="h-4 w-4" />
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{analytics.totalEntries}</div>
          <div className="text-sm text-gray-600">Total Entries</div>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {getMoodEmoji(analytics.avgMood)} {analytics.avgMood}
          </div>
          <div className="text-sm text-gray-600">Avg Mood</div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center p-3 bg-purple-50 rounded-lg">
          <div className="text-xl font-bold text-purple-600">{analytics.streak}</div>
          <div className="text-sm text-gray-600">Day Streak</div>
        </div>
        <div className="text-center p-3 bg-orange-50 rounded-lg">
          <div className={`text-xl font-bold ${getTrendColor(analytics.recentTrend)}`}>
            {getTrendIcon(analytics.recentTrend)}
          </div>
          <div className="text-sm text-gray-600">Trend</div>
        </div>
      </div>
    </Card>
  );
};

export default MoodAnalyticsSummary;
